import { FeedbackAlert as Alert } from "src/shared/ui/src/feedback/FeedbackAlert";
import {
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import {
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LockOutlined, SaveOutlined } from "@mui/icons-material";
import dico from "../../shared/util/src/language/dico";
import { formatPositionRack } from "../../shared/util/src/position-rack";
import {
  entryDisplayValue,
  isEntryEquipmentField,
  isEntryValueAllowed,
  type EntryCell,
  type EntryReceipt,
  type EntryRequest,
} from "../../shared/util/src/athlete-entry";

export type EquipmentDraft = { value: string; version: string };
export type EquipmentDrafts = Record<string, EquipmentDraft>;
type EquipmentUpdate = Pick<EntryRequest, "field" | "value" | "version">;
export type EquipmentOutcome = {
  id: string;
  status: string;
  receipt?: EntryReceipt;
};
export type EquipmentSubmission = { id: string; drafts: EquipmentDrafts };

// EXT is a displayed proposal until it has actually been stored by the secretary.
function withEquipmentDefaults(
  cells: EntryCell[],
  drafts: EquipmentDrafts,
): EquipmentDrafts {
  const rack = cells.find(
    (cell) =>
      cell.field === "positionRack" &&
      cell.value == null &&
      cell.editable &&
      cell.options?.some(
        (option) => option.value === 0 && option.label === "EXT",
      ),
  );
  return rack && !drafts[rack.field]
    ? { ...drafts, [rack.field]: { value: "0", version: rack.version } }
    : drafts;
}

function equipmentSigningDrafts(
  cells: EntryCell[],
  drafts: EquipmentDrafts,
  includeUnchanged: boolean,
): EquipmentDrafts {
  const current = includeUnchanged
    ? Object.fromEntries(
        cells
          .filter((c) => c.editable && c.value != null)
          .map((c) => [
            c.field,
            { value: String(c.value), version: c.version },
          ]),
      )
    : {};
  return withEquipmentDefaults(cells, { ...current, ...drafts });
}

function changedEquipment(
  cells: EntryCell[],
  drafts: EquipmentDrafts,
  includeUnchanged = false,
) {
  return cells.filter((cell) => {
    const draft = drafts[cell.field];
    return (
      (cell.kind === "height" || isEntryEquipmentField(cell.field)) &&
      !!draft &&
      draft.value.trim() !== "" &&
      (includeUnchanged || Number(draft.value) !== cell.value)
    );
  });
}

/** Build a partial atomic update: blank, unchanged and non-equipment fields stay untouched. */
export function buildEquipmentRequest(
  cells: EntryCell[],
  drafts: EquipmentDrafts,
  includeUnchanged = false,
): Omit<EntryRequest, "id"> | undefined {
  drafts = equipmentSigningDrafts(cells, drafts, includeUnchanged);
  const changed = changedEquipment(cells, drafts, includeUnchanged);
  if (!changed.length) return undefined;
  for (const cell of changed) {
    if (!cell.editable) throw Error("LOCKED");
    if (drafts[cell.field].version !== cell.version) throw Error("CONFLICT");
    if (!isEntryValueAllowed(cell, Number(drafts[cell.field].value)))
      throw Error("INVALID_VALUE");
  }
  const [primary, ...updates]: EquipmentUpdate[] = changed.map((cell) => ({
    field: cell.field,
    value: Number(drafts[cell.field].value),
    version: drafts[cell.field].version,
  }));
  return { ...primary, ...(updates.length ? { updates } : {}) };
}

/** Only a matching committed receipt clears the exact drafts included in that submission. */
export function reconcileEquipmentDrafts(
  drafts: EquipmentDrafts,
  submission: EquipmentSubmission,
  outcome: EquipmentOutcome,
): EquipmentDrafts {
  if (submission.id !== outcome.id || outcome.receipt?.status !== "applied")
    return drafts;
  const next = { ...drafts };
  for (const [field, sent] of Object.entries(submission.drafts)) {
    if (
      drafts[field]?.value === sent.value &&
      drafts[field]?.version === sent.version
    )
      delete next[field];
  }
  return next;
}

function label(cell: EntryCell): string {
  const t = dico().athleteEntry;
  return (
    cell.label || (t[cell.field as keyof typeof t] as string) || cell.field
  );
}

function displayed(cell: EntryCell, value: string | number | null): string {
  if (value == null || value === "") return "-";
  const option = cell.options?.find((o) => o.value === Number(value));
  return entryDisplayValue(cell.field, Number(value), option?.label);
}

function errorMessage(reason: string): string {
  const t = dico().athleteEntry;
  return t.errors[reason as keyof typeof t.errors] || t.error;
}

/** One equipment form, with drafts owned by the page and commit acknowledgement owned by the server. */
export default function EquipmentForm({
  cells,
  drafts: suppliedDrafts,
  onDraftsChange,
  busy,
  pending,
  outcomes,
  submit,
  requireSignature = false,
}: {
  cells: EntryCell[];
  drafts: EquipmentDrafts;
  onDraftsChange: Dispatch<SetStateAction<EquipmentDrafts>>;
  busy: boolean;
  requireSignature?: boolean;
  pending: EntryRequest | null;
  outcomes: EquipmentOutcome[];
  /** Resolves with the UUID after relay acknowledgement; this is not a commit acknowledgement. */
  submit: (request: Omit<EntryRequest, "id">) => Promise<string>;
}) {
  const t = dico().athleteEntry;
  const drafts = equipmentSigningDrafts(
    cells,
    suppliedDrafts,
    requireSignature,
  );
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submission, setSubmission] = useState<EquipmentSubmission>();
  const submissionInFlight = useRef(false);
  const changed = changedEquipment(cells, drafts, requireSignature);
  const conflicts = changed.filter(
    (cell) => drafts[cell.field].version !== cell.version,
  );
  const invalid = changed.filter(
    (cell) => !isEntryValueAllowed(cell, Number(drafts[cell.field].value)),
  );
  const locked = changed.some((cell) => !cell.editable);
  const disabled =
    busy ||
    submitting ||
    !changed.length ||
    !!conflicts.length ||
    !!invalid.length ||
    locked;
  const frozen = pending ? [pending, ...(pending.updates || [])] : [];

  useEffect(() => {
    if (!submission) return;
    const outcome = outcomes.find((row) => row.id === submission.id);
    if (!outcome || ["pending", "executing"].includes(outcome.status)) return;
    if (outcome.receipt?.status === "applied") {
      onDraftsChange((current) =>
        reconcileEquipmentDrafts(current, submission, outcome),
      );
      setError("");
    } else {
      setError(
        outcome.receipt?.reason ||
          (outcome.status === "cancelled" ? "COMMAND_CANCELLED" : "CONFLICT"),
      );
    }
    setSubmission(undefined);
  }, [submission, outcomes, onDraftsChange]);

  return (
    <Paper
      component="form"
      aria-label={t.equipmentSettings}
      noValidate
      variant="outlined"
      sx={{ p: 2, borderRadius: 3 }}
      onSubmit={async (event) => {
        event.preventDefault();
        if (disabled || submissionInFlight.current) return;
        const request = buildEquipmentRequest(cells, drafts, requireSignature);
        if (!request) return;
        const sent = [request, ...(request.updates || [])];
        const sentDrafts = Object.fromEntries(
          sent.map((update) => [update.field, { ...drafts[update.field] }]),
        );
        submissionInFlight.current = true;
        setSubmitting(true);
        setError("");
        try {
          const id = await submit(request);
          setSubmission({ id, drafts: sentDrafts });
        } catch (failure) {
          setError((failure as Error).message);
        } finally {
          submissionInFlight.current = false;
          setSubmitting(false);
        }
      }}
    >
      <Stack spacing={2}>
        {!!cells.length && cells.every((cell) => !cell.editable) && (
          <Chip
            size="small"
            variant="outlined"
            icon={<LockOutlined />}
            label={t.locked}
            sx={{ alignSelf: "flex-start" }}
          />
        )}
        {!cells.length && (
          <Typography variant="body2" color="text.secondary">
            {t.noHeightsAvailable}
          </Typography>
        )}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            columnGap: 1.5,
            rowGap: 2,
            pt: 0.5,
          }}
        >
          {cells.map((cell) => {
            const draft = drafts[cell.field];
            const pendingValue = frozen.find(
              (update) => update.field === cell.field,
            )?.value;
            const value = pendingValue ?? draft?.value ?? cell.value ?? "";
            const isInvalid = invalid.some((row) => row.field === cell.field);
            return (
              <TextField
                key={cell.field}
                fullWidth
                size="small"
                label={label(cell)}
                value={value}
                disabled={busy || submitting || !cell.editable}
                error={isInvalid}
                select={!!cell.options}
                type={cell.options ? undefined : "number"}
                inputProps={{
                  min: cell.min,
                  max: cell.max,
                  step: cell.step,
                  inputMode: "decimal",
                }}
                InputLabelProps={{ shrink: true }}
                sx={{
                  ".MuiOutlinedInput-root": { borderRadius: 2 },
                  ".MuiInputBase-input": {
                    fontSize: 16,
                    fontVariantNumeric: "tabular-nums",
                  },
                  ".MuiInputBase-input.Mui-disabled": {
                    color: "text.secondary",
                    WebkitTextFillColor: "currentColor",
                  },
                  ".MuiInputLabel-root.Mui-disabled": {
                    color: "text.secondary",
                  },
                }}
                helperText={
                  isInvalid
                    ? `${t.invalidWeight} · ${t.min}: ${cell.min} · ${t.step}: ${cell.step}`
                    : undefined
                }
                onChange={(event) => {
                  const nextValue = event.target.value;
                  onDraftsChange((current) => ({
                    ...current,
                    [cell.field]: {
                      value: nextValue,
                      version: current[cell.field]?.version || cell.version,
                    },
                  }));
                  setError("");
                }}
              >
                {cell.options && <MenuItem value="">-</MenuItem>}
                {cell.options?.map((option) => (
                  <MenuItem key={option.value} value={String(option.value)}>
                    {cell.field === "positionRack"
                      ? formatPositionRack(option.label)
                      : option.label}
                  </MenuItem>
                ))}
              </TextField>
            );
          })}
        </Box>
        {conflicts.map((cell) => (
          <Alert key={cell.field} severity="warning" sx={{ borderRadius: 2 }}>
            {t.draftChanged}
            <Typography variant="body2">
              {label(cell)} · {t.current}: {displayed(cell, cell.value)} ·{" "}
              {t.proposed}: {displayed(cell, drafts[cell.field].value)}
            </Typography>
            <Button
              size="small"
              disabled={busy || submitting || !cell.editable}
              onClick={() => {
                onDraftsChange((current) =>
                  current[cell.field]
                    ? {
                        ...current,
                        [cell.field]: {
                          ...current[cell.field],
                          version: cell.version,
                        },
                      }
                    : current,
                );
                setError("");
              }}
            >
              {t.useCurrentRules}
            </Button>
          </Alert>
        ))}
        {error && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {errorMessage(error)}
          </Alert>
        )}
        {!!cells.length && (
          <Button
            type="submit"
            variant="contained"
            disableElevation
            fullWidth
            startIcon={<SaveOutlined />}
            disabled={disabled}
            sx={{
              minHeight: 44,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {t.signAndSend}
          </Button>
        )}
      </Stack>
    </Paper>
  );
}
